/**
 * A tenant moved to a different plan. Platform-operator event — see
 * `docs/NOTIFICATION_CHANNELS.md` for which write owns the emit, because the transition is
 * applied in several places and a caller that only delegates must stay silent.
 */

import type { PlatformEventDefinition, PlatformEventPayloadBase } from './definition'

export const PLATFORM_TENANT_PLAN_CHANGED_EVENT_TYPE = 'platform_tenant_plan_changed' as const

export type PlanChangeDirection = 'upgrade' | 'downgrade' | 'lateral'

/** What applied the change. `stripe_sync` covers anything Stripe echoed back to us. */
export type PlanChangeSource =
  | 'self_serve'
  | 'admin'
  | 'checkout'
  | 'stripe_sync'
  | 'shopify'
  | 'cancellation'
  | 'channel_conversion'

export interface PlatformTenantPlanChangedPayload extends PlatformEventPayloadBase {
  fromPlanId: string
  fromPlanName: string
  toPlanId: string
  toPlanName: string
  direction: PlanChangeDirection
  source: PlanChangeSource
}

const DIRECTIONS: readonly PlanChangeDirection[] = ['upgrade', 'downgrade', 'lateral']

const SOURCES: readonly PlanChangeSource[] = [
  'self_serve',
  'admin',
  'checkout',
  'stripe_sync',
  'shopify',
  'cancellation',
  'channel_conversion',
]

const SOURCE_LABELS: Record<PlanChangeSource, string> = {
  self_serve: 'Changed by the tenant',
  admin: 'Changed by platform staff',
  checkout: 'Activated after checkout',
  stripe_sync: 'Applied by Stripe',
  shopify: 'Activated on Shopify',
  cancellation: 'Subscription ended',
  channel_conversion: 'Billing channel migration',
}

function isPlanChangedPayload(payload: unknown): payload is PlatformTenantPlanChangedPayload {
  if (typeof payload !== 'object' || payload === null) return false
  const p = payload as Record<string, unknown>
  return (
    typeof p.tenantId === 'string' &&
    typeof p.tenantName === 'string' &&
    typeof p.fromPlanId === 'string' &&
    typeof p.fromPlanName === 'string' &&
    typeof p.toPlanId === 'string' &&
    typeof p.toPlanName === 'string' &&
    DIRECTIONS.includes(p.direction as PlanChangeDirection) &&
    SOURCES.includes(p.source as PlanChangeSource)
  )
}

export const platformTenantPlanChangedEventDefinition: PlatformEventDefinition<PlatformTenantPlanChangedPayload> =
  {
    eventType: PLATFORM_TENANT_PLAN_CHANGED_EVENT_TYPE,
    supportedChannelTypes: ['email', 'telegram'],
    validatePayload: isPlanChangedPayload,
    system: {
      label: 'Tenant changed plan',
      description:
        'A tenant moved to a different plan — an upgrade, a downgrade, or a move that ended its subscription.',
      summarize: (payload) => ({
        title: `${payload.tenantName}: ${payload.fromPlanName} → ${payload.toPlanName}`,
        facts: [
          { label: 'Tenant', value: payload.tenantName },
          { label: 'From', value: payload.fromPlanName },
          { label: 'To', value: payload.toPlanName },
          { label: 'Direction', value: payload.direction },
          { label: 'Cause', value: SOURCE_LABELS[payload.source] },
        ],
        href: '/?page=billing-admin&tab=tenants',
      }),
    },
  }
